import { gql, useMutation } from "@apollo/client";
import { AppContext } from "App";
import Button from "components/base/Button";
import { Field, Input, Select, Switcher, Text } from "components/base/Form";
import { Alert } from "components/base/Toast";
import { useModals } from "ModalProvider";
import { useContext, useEffect, useState } from "react";
import { MANAGE_PRODUCT_LINE } from "utils/permissions";
import ConfirmImportFromGTMProjectView from "./ConfirmImportFromGTMProjectView";
import { FRAGMENT_PRODUCT_LINE_DETAIL } from "./graphql";
import GTMProjectSelectorView from "./GTMProjectSelectorView";
import MaterialsInput from "./MaterialsInput";
import ProductLineImagesView from "./ProductLineImagesView";
import { FETCH_PRODUCT_LINES } from "./ProductLines";
import ProductListSelector from "./ProductListSelector";
import ProductWarningsSelector from "./ProductWarningsSelector";

const newSimpleFields = {
  name: "",
  nameForOdooTemplate: "",
  number: "",
  hsCode: "",
  hsCodeForEu: "",
  hsCodeForUs: "",
  age: "",
  pantone: "",
  printingMethod: "",
  customsName: "",
  customsBrand: "",
  customsUsage: "",
  customsValue: 0,
  customsSize: "",
  customsMaterials: "",
  activeForWis: true,
  fromProductListId: null,
  description: "",
};

const SAVE_PRODUCT_LINE = gql`
  mutation SAVE_PRODUCT_LINE(
    $id: ID
    $simpleFields: ProductLineSimpleFields
    $materials: [ProductLineMaterialInputType]
    $childrenIds: [ID]
    $warningIds: [ID]
    $labTestStandardIds: [ID]
  ) {
    saveProductLine(
      id: $id
      simpleFields: $simpleFields
      materials: $materials
      warningIds: $warningIds
      childrenIds: $childrenIds
      labTestStandardIds: $labTestStandardIds
    ) {
      productLine {
        ...productLine
      }
    }
  }
  ${FRAGMENT_PRODUCT_LINE_DETAIL}
`;

const ProductLineForm = ({
  id,
  fetchedProductLine,
  onSave,
  duplicate,
  duplicatingProduct,
}) => {
  const { hasPermission } = useContext(AppContext);

  const gtmProjectsModal = useModals();
  const confirmModal = useModals();

  const [simpleFields, setSimpleFields] = useState(newSimpleFields);
  const [materials, setMaterials] = useState([]);
  const [warningIds, setWarningIds] = useState([]);
  const [childrenIds, setChildrenIds] = useState([]);
  const [labTestStandardIds, setLabTestStandardIds] = useState([]);

  const [isCombo, setIsCombo] = useState(false);

  const [saveProductLine, saveProductLineRes] = useMutation(SAVE_PRODUCT_LINE, {
    onCompleted: (res) => {
      Alert("success", "Saved.");
      if (onSave) onSave(res.saveProductLine.productLine);
    },
    onError: (error) => Alert("error", error.message),
    refetchQueries: id === null ? [{ query: FETCH_PRODUCT_LINES }] : [],
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    if (fetchedProductLine) {
      const line = fetchedProductLine;
      setSimpleFields({
        name: line.name,
        number: line.number,
        nameForOdooTemplate: line.nameForOdooTemplate,
        hsCode: line.hsCode,
        hsCodeForEu: line.hsCodeForEu,
        hsCodeForUs: line.hsCodeForUs,
        age: line.age,
        pantone: line.pantone,
        printingMethod: line.printingMethod,
        customsName: line.customsName,
        customsBrand: line.customsBrand,
        customsUsage: line.customsUsage,
        customsValue: line.customsValue,
        customsSize: line.customsSize,
        customsMaterials: line.customsMaterials,
        activeForWis: line.activeForWis,
        fromProductListId: line.fromProductList?.id,
        description: line.description,
      });
      setMaterials(
        line.materials.map((i) => ({
          name: i.material.name,
          percentage: i.percentage,
          remark: i.remark,
        })),
      );
      setWarningIds(line.warnings.map((i) => i.id));
      setLabTestStandardIds(line.labTestStandards.map((i) => i.id));
      setIsCombo(line.children.length > 0);
      setChildrenIds(line.children.map((i) => i.id));
    }
  }, [fetchedProductLine]);

  useEffect(() => {
    if (duplicatingProduct) {
      setSimpleFields(duplicatingProduct.simpleFields);
      setMaterials(duplicatingProduct.materials);
      setWarningIds(duplicatingProduct.warningIds);
      setLabTestStandardIds(duplicatingProduct.labTestStandardIds);
      setIsCombo(duplicatingProduct.isCombo);
      setChildrenIds(duplicatingProduct.childrenIds);
    }
  }, [duplicatingProduct]);

  function importInfoFromGTMPRojectHandler() {
    gtmProjectsModal.present({
      title: "Choose GTM Project",
      children: <GTMProjectSelectorView onSelect={onSelectProject} />,
    });
  }

  function onSelectProject(project) {
    gtmProjectsModal.hide();

    confirmModal.present({
      title: "Confirm Import",
      subtitle: project.name,
      children: (
        <ConfirmImportFromGTMProjectView
          project={project}
          onConfirm={onConfirmImportFromProject}
        />
      ),
    });
  }

  function onConfirmImportFromProject(logisticsData) {
    if (!logisticsData) return;

    setSimpleFields((prev) => ({
      ...prev,
      hsCode:
        logisticsData.hsCode !== undefined ? logisticsData.hsCode : prev.hsCode,
      hsCodeForEu:
        logisticsData.hsCodeForEu !== undefined
          ? logisticsData.hsCodeForEu
          : prev.hsCodeForEu,
      hsCodeForUs:
        logisticsData.hsCodeForUs !== undefined
          ? logisticsData.hsCodeForUs
          : prev.hsCodeForUs,
      customsName:
        logisticsData.customsName !== undefined
          ? logisticsData.customsName
          : prev.customsName,
      customsBrand:
        logisticsData.customsBrand !== undefined
          ? logisticsData.customsBrand
          : prev.customsBrand,
      customsUsage:
        logisticsData.customsUsage !== undefined
          ? logisticsData.customsUsage
          : prev.customsUsage,
      customsValue:
        logisticsData.customsValue !== undefined
          ? logisticsData.customsValue
          : prev.customsValue,
      customsSize:
        logisticsData.customsSize !== undefined
          ? logisticsData.customsSize
          : prev.customsSize,
      customsMaterials:
        logisticsData.customsMaterials !== undefined
          ? logisticsData.customsMaterials
          : prev.customsMaterials,
    }));
    confirmModal.hide();
    Alert("success", "Imported customs and HS fields from GTM project.");
  }

  function onChangeSimpleFields(field, value) {
    setSimpleFields((prev) => ({ ...prev, [field]: value }));
  }

  function validInput() {
    if (simpleFields.name === "") {
      Alert("error", "At least give me a name...");
      return false;
    }
    if (simpleFields.number === "") {
      Alert("error", "Item number is required.");
      return false;
    }
    return true;
  }

  const variables = {
    id,
    simpleFields: {
      ...simpleFields,
      age: simpleFields.age !== "" ? simpleFields.age : 0,
    },
    warningIds,
    materials: materials.map((i) => ({
      ...i,
      percentage: i.percentage ? i.percentage : 0,
      remark: i.remark ? i.remark : "",
    })),
    childrenIds,
    labTestStandardIds,
  };

  return (
    <div className="space-y-8 p-10 pb-0 text-xs h-full">
      {hasPermission(MANAGE_PRODUCT_LINE) ? (
        <>
          <div className="grid grid-cols-12 gap-8">
            <div className="card px-6 py-4 col-span-8">
              <h5>Base Info</h5>
              <div className="mt-4 space-y-4">
                <Field
                  label="name"
                  value={simpleFields.name}
                  onChange={(value) => onChangeSimpleFields("name", value)}
                />
                <Field
                  label="number"
                  value={simpleFields.number}
                  onChange={(value) => onChangeSimpleFields("number", value)}
                  tips="The core number without considering the package part. For example: 101, 102, 301, 302."
                />

                <Field
                  label="Name for Odoo Template"
                  value={simpleFields.nameForOdooTemplate}
                  onChange={(value) =>
                    onChangeSimpleFields("nameForOdooTemplate", value)
                  }
                  tips="This field is used to support have different name between wis and odoo for the same odoo template."
                />

                <section className="space-y-4">
                  <div className="flex space-x-4 items-center">
                    <label>HS Code:</label>
                    <Input
                      placeholder="HS Code"
                      value={simpleFields.hsCode}
                      onChange={(e) =>
                        onChangeSimpleFields("hsCode", e.target.value)
                      }
                    />

                    <label>EU:</label>
                    <Input
                      placeholder="HS Code for EU"
                      value={simpleFields.hsCodeForEu}
                      onChange={(e) =>
                        onChangeSimpleFields("hsCodeForEu", e.target.value)
                      }
                    />

                    <label>US:</label>
                    <Input
                      placeholder="HS Code for US"
                      value={simpleFields.hsCodeForUs}
                      onChange={(e) =>
                        onChangeSimpleFields("hsCodeForUs", e.target.value)
                      }
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <label htmlFor="">Age Grading:</label>
                    <Select
                      value={simpleFields.age}
                      onChange={(e) =>
                        onChangeSimpleFields("age", e.target.value)
                      }
                    >
                      {[...Array(61).keys()].map((i) => (
                        <option key={i} value={i}>
                          {i}
                        </option>
                      ))}
                    </Select>
                  </div>

                  <Field
                    label="pantone"
                    value={simpleFields.pantone}
                    onChange={(value) => onChangeSimpleFields("pantone", value)}
                  />

                  <Field
                    label="Printing Method"
                    value={simpleFields.printingMethod}
                    onChange={(value) =>
                      onChangeSimpleFields("printingMethod", value)
                    }
                  />

                  <div className="flex space-x-4 items-baseline">
                    <label htmlFor="">Materials: </label>
                    <MaterialsInput
                      className="flex-1"
                      materials={materials}
                      setMaterials={setMaterials}
                    />
                  </div>

                  <div className="flex items-baseline space-x-4">
                    <label>Warnings: </label>
                    <ProductWarningsSelector
                      warningIds={warningIds}
                      setWarningIds={setWarningIds}
                    />
                  </div>
                </section>

                <div className="flex space-x-4 items-baseline">
                  <label className="capitalize w-auto">description: </label>
                  <Text
                    placeholder="Input description here..."
                    value={simpleFields.description}
                    onChange={(e) => {
                      onChangeSimpleFields("description", e.target.value);
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="col-span-4 space-y-8">
              <ProductLineImagesView id={id} />

              <div className="card px-6 py-4">
                <h5>Product List</h5>
                <div className="mt-1 text-xs opacity-70">
                  Product List is currently used for displaying test reports in
                  BIZ.
                </div>
                <div className="mt-4">
                  <ProductListSelector
                    value={simpleFields.fromProductListId}
                    onChange={(id) =>
                      onChangeSimpleFields("fromProductListId", id)
                    }
                  />
                </div>
              </div>

              <div className="card px-6 py-4">
                <h5>Status</h5>
                <div className="mt-4 space-y-4">
                  <div className="flex space-x-4 items-center">
                    <label htmlFor="">Active for WIS: </label>
                    <Switcher
                      isOn={simpleFields.activeForWis}
                      onChange={() =>
                        onChangeSimpleFields(
                          "activeForWis",
                          !simpleFields.activeForWis,
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card px-6 py-4">
            <h5>Customs Info</h5>
            <div className="space-y-4 mt-4">
              <div className="flex space-x-8 mt-6">
                <Field
                  label="Name"
                  value={simpleFields.customsName}
                  onChange={(value) =>
                    onChangeSimpleFields("customsName", value)
                  }
                />
                <Field
                  label="Brand"
                  value={simpleFields.customsBrand}
                  onChange={(value) =>
                    onChangeSimpleFields("customsBrand", value)
                  }
                />
                <Field
                  label="Usage"
                  value={simpleFields.customsUsage}
                  onChange={(value) =>
                    onChangeSimpleFields("customsUsage", value)
                  }
                />

                <Field
                  label="Value"
                  value={simpleFields.customsValue}
                  onChange={(value) =>
                    onChangeSimpleFields("customsValue", value)
                  }
                />
              </div>

              <Field
                label="Size"
                value={simpleFields.customsSize}
                onChange={(value) => onChangeSimpleFields("customsSize", value)}
              />

              <Field
                label="Materials"
                value={simpleFields.customsMaterials}
                onChange={(value) =>
                  onChangeSimpleFields("customsMaterials", value)
                }
              />

              <div className="opacity-70 text-xs">
                We will use the item size in customs form. For materials we will
                setup cn for it later.
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="card px-6 py-4 col-span-8">
            <h5>Base Info</h5>
            <div className="mt-4 space-y-4">
              <Field label="name" value={simpleFields.name} />

              <div className="flex space-x-4 items-baseline">
                <label className="capitalize w-auto">description: </label>
                <Text
                  placeholder="Input description here..."
                  value={simpleFields.description}
                  onChange={(e) => {
                    onChangeSimpleFields("description", e.target.value);
                  }}
                />
              </div>
            </div>
          </div>
        </>
      )}

      <section className="flex space-x-8 sticky bottom-0 -mx-10 px-10 py-4 dark:bg-opacity-70 dark:bg-gray-800 backdrop-blur-lg text-base border-t dark:border-gray-700">
        <Button
          loading={saveProductLineRes.loading}
          onClick={() => {
            if (validInput()) saveProductLine({ variables });
          }}
        >
          Save
        </Button>

        {id && hasPermission() ? (
          <>
            <Button
              onClick={() =>
                duplicate({
                  simpleFields: {
                    ...simpleFields,
                    name: `${simpleFields.name} duplicated`,
                    number: simpleFields.number + " duplicated",
                  },
                  materials,
                  warningIds,
                  childrenIds,
                  labTestStandardIds,
                  isCombo,
                })
              }
            >
              Duplicate
            </Button>
          </>
        ) : null}

        <Button onClick={importInfoFromGTMPRojectHandler}>
          Import Info from GTM Project
        </Button>
      </section>
    </div>
  );
};

export default ProductLineForm;
